import footprint, { FootprintComponentKind } from "@onefootprint/footprint-js";
import {
  IRequirementResponse,
  PayeeTaxDocumentSharePermission
} from "@wingspanhq/payments/dist/interfaces";
import { IIdentityVerificationRequestResponse } from "@wingspanhq/users";
import { IdentityVerificationRequestStatus } from "@wingspanhq/users/dist/lib/interfaces";
import { useCallback } from "react";
import { useStartIdentityVerification } from "../../../../../../query/identityVerification/mutations/useStartIdentityVerification";
import { footprintAppearanceConfig } from "../../../../../../shared/constants/footprint";
import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { RequirementCardContent } from "../../../../components/RequirementCardContent";
import { RequirementInstructionsView } from "../../../../components/RequirementInstructionsView/RequirementInstructionsView";
import { selectorIsRequirementWithApprovalFlow } from "../../../../selectors/selectorIsRequirementWithApprovalFlow";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementTableCallback
} from "../../../../types";
import { ALL_REQUIREMENT_EVENTS } from "../../../../utils";
import { useModalShareInformationWithClient } from "../../components/ModalShareInformationWithClient/useModalFormW9Consent";
import { ValidationProgress } from "../../components/ValidationProgress";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../instructions";
import { getIdentityVerificationPayeeRequirementStatus } from "../helpers/getIdentityVerificationPayeeRequirementStatus";
import { getIdentityVerificationPayerProgressSteps } from "../helpers/getIdentityVerificationPayerProgressSteps";
import { getIdentityVerificationRequirementName } from "../helpers/getIdentityVerificationRequirementName";
import { getRequirementPayerFromCache } from "../helpers/getRequirementPayerFromCache";
import { useIDVPendingSuccessStatus } from "../utils";
import { payerIdentityVerificationRenderCardDescription } from "./payerIdentityVerificationRenderCardDescription";

export const usePayerIdentityVerificationConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  IIdentityVerificationRequestResponse | undefined
> => {
  const shareInfoModal = useModalShareInformationWithClient();

  const {
    setPendingSuccessStatus,
    getIsPendingSuccessStatus: isPendingSuccess
  } = useIDVPendingSuccessStatus();

  const [startIdentityVerification, startIdentityVerificationMeta] =
    useStartIdentityVerification();

  const openFootprint = useCallback(
    async (
      requirement: IRequirementResponse,
      identityVerificationRequest: IIdentityVerificationRequestResponse,
      token: string
    ) => {
      const onClose = () => {
        console.log("IdentityVerification onClose");
      };

      const onCancel = () => {
        console.log("IdentityVerification onCancel");
      };

      const component = footprint.init({
        appearance: footprintAppearanceConfig,
        authToken: token,
        kind: FootprintComponentKind.Verify,
        variant: "modal",
        containerId: "root",
        onCancel: onCancel,
        onClose: onClose,
        options: {
          showLogo: false
        },
        onComplete: () => {
          setPendingSuccessStatus(requirement, identityVerificationRequest);
          console.log("IdentityVerification onComplete");
          callback(ALL_REQUIREMENT_EVENTS.CompleteVerification, requirement);
          component.destroy();
        }
      });

      component.render();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    []
  );

  const getConsentAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    identityVerificationRequest:
      | IIdentityVerificationRequestResponse
      | undefined
  ) => {
    const payer = getRequirementPayerFromCache(requirement, context);

    if (
      identityVerificationRequest?.status ===
        IdentityVerificationRequestStatus.Pending &&
      payer &&
      payer?.payeeOwnedData.shareTaxDocument !==
        PayeeTaxDocumentSharePermission.Allow
    ) {
      return async () => {
        await shareInfoModal.open({ payer });
      };
    }

    return undefined;
  };

  const getStartAction = (
    requirement: IRequirementResponse,
    context: ContextItem,
    identityVerificationRequest:
      | IIdentityVerificationRequestResponse
      | undefined
  ) => {
    const consentAction = getConsentAction(
      requirement,
      context,
      identityVerificationRequest
    );

    if (
      !consentAction &&
      !isPendingSuccess(requirement, identityVerificationRequest) &&
      identityVerificationRequest?.status ===
        IdentityVerificationRequestStatus.Pending
    ) {
      return async () => {
        await startIdentityVerification(identityVerificationRequest, {
          onSuccess: response => {
            const token = response.identityVerificationToken;

            if (!token) {
              return;
            }
            openFootprint(requirement, identityVerificationRequest, token);
          }
        });
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, identityVerificationRequest) => {
      return isPendingSuccess(requirement, identityVerificationRequest)
        ? false
        : identityVerificationRequest?.status ===
            IdentityVerificationRequestStatus.Pending;
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      identityVerificationRequest
    ) => {
      return identityVerificationRequest?.status
        ? getIdentityVerificationPayerProgressSteps({
            status: identityVerificationRequest.status,
            isPendingSuccessStatus: isPendingSuccess(
              requirement,
              identityVerificationRequest
            ),
            reviewStrategy: requirement.reviewStrategy
          })
        : undefined;
    },
    requirementStatusAlert: (
      requirement,
      context,
      identityVerificationRequest
    ) => {
      if (
        identityVerificationRequest?.status ===
        IdentityVerificationRequestStatus.Pending
      ) {
        return {
          theme: "warning",
          title: "Action required",
          children:
            "Complete the requirement in order to be eligible to receive payments from your client.",
          icon: "alert"
        };
      } else if (
        identityVerificationRequest?.status ===
        IdentityVerificationRequestStatus.Failed
      ) {
        return {
          theme: "error",
          title: "Failed",
          children:
            "Something went wrong with your ID verification. Please contact your client to resolve the issue.",
          icon: "alert-circle"
        };
      }
    },
    requirementName: getIdentityVerificationRequirementName,
    requirementInstructions: (requirement, context) => {
      return () => {
        return (
          <RequirementInstructionsView
            instructions={IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS}
          />
        );
      };
    },
    requirementDetails: (requirement, context, identityVerificationRequest) => {
      const startAction = getStartAction(
        requirement,
        context,
        identityVerificationRequest
      );

      const consentAction = getConsentAction(
        requirement,
        context,
        identityVerificationRequest
      );

      const payer = getRequirementPayerFromCache(requirement, context);

      const isApprovalFlow = selectorIsRequirementWithApprovalFlow(requirement);

      return {
        header: {
          label: {
            text: "Identity verification"
          },
          value: {
            pill: isApprovalFlow
              ? undefined
              : identityVerificationRequest?.status ===
                IdentityVerificationRequestStatus.Failed
              ? {
                  theme: "error",
                  text: "Failed",
                  icon: true
                }
              : undefined
          }
        },
        children: startIdentityVerificationMeta.isError ? (
          <RequirementCardContent
            type="people"
            title="Something went wrong with your ID verification"
            description={
              <>
                Something went wrong while processing your verification. Please
                try again or contact support if this continues!
              </>
            }
          />
        ) : isPendingSuccess(requirement, identityVerificationRequest) ? (
          <ValidationProgress
            requirement={requirement}
            identityVerificationRequest={identityVerificationRequest!}
            onSuccess={callback}
          />
        ) : (
          payerIdentityVerificationRenderCardDescription(
            requirement,
            identityVerificationRequest,
            payer
          )
        ),
        data: [],
        actions:
          startAction || consentAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Start ID verification",
                    onAsyncClick: startAction,
                    kind: "Primary",
                    visible: !!startAction
                  },
                  {
                    label: "Consent to share information",
                    onAsyncClick: consentAction,
                    kind: "Primary",
                    visible: !!consentAction
                  }
                ]
              }
            : undefined
      };
    },
    toolbarActions: (requirement, context, identityVerificationRequest) => {
      const actions: RequirementAction[] = [];

      const startAction = getStartAction(
        requirement,
        context,
        identityVerificationRequest
      );

      const consentAction = getConsentAction(
        requirement,
        context,
        identityVerificationRequest
      );

      if (consentAction) {
        actions.push({
          label: "Consent to share information",
          onAsyncClick: consentAction,
          buttonKind: "Primary",
          icon: "open-tab",
          buttonIcon: null,
          mainAction: true
        });
      }

      if (startAction) {
        actions.push({
          label: "Start ID verification",
          onAsyncClick: startAction,
          buttonKind: "Primary",
          icon: "open-tab",
          buttonIcon: null,
          mainAction: true
        });
      }

      return actions;
    },
    nameCell: (requirement, context, identityVerificationRequest) => {
      return {
        text: getIdentityVerificationRequirementName(
          requirement,
          context,
          identityVerificationRequest
        )
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType
      }),
    statusCell: (requirement, context, identityVerificationRequest) => {
      return getIdentityVerificationPayeeRequirementStatus(
        requirement,
        context,
        identityVerificationRequest
      );
    }
  };
};
